import { decodeFunctionData, decodeFunctionResult, encodeFunctionData, recoverAddress, type Hex } from "viem";
import { ponsRewardNftABI } from "../../../abis/pons-reward-nft";
import type { PreparedNftTransaction } from "../../../wallet/transaction-preflight";
import type { Eip1193Provider } from "../../../wallet/eip1193";
import { readRpc, type RpcReadOptions } from "../../../wallet/read-rpc";
import { contractReadError } from "../../../contracts/contract-errors";
import { ELYSIUM, isElysiumFreePolicy } from "../../../config/elysium";

export function assertTaskMintCall(input: PreparedNftTransaction) {
  const decoded = decodeFunctionData({ abi: ponsRewardNftABI, data: input.data });
  const identity = input.taskMint;
  if (decoded.functionName !== "mint" || !identity || identity.route !== "SOCIAL_TASK") {
    throw new Error("Unreviewed social task mint");
  }
  const [authorization, signature] = decoded.args;
  if (input.chainId === ELYSIUM.chainId && !isElysiumFreePolicy(authorization.rewardPolicyVersion, authorization.rewardPolicyHash) ||
    input.valueWei !== 0n || authorization.selectedTierUsd !== 0n || authorization.quotedMintFeeWei !== 0n ||
    identity.tierUsd !== "0" || identity.feeWei !== "0" ||
    !same(authorization.wallet, input.account) || !same(authorization.sourceToken, identity.sourceToken) ||
    !same(authorization.proofKey, identity.proofKey) || !same(authorization.authorizationId, identity.authorizationId) ||
    !same(authorization.rewardPolicyHash, identity.policyHash) ||
    authorization.rewardPolicyVersion.toString() !== identity.policyVersion ||
    !/^0x[0-9a-f]{130}$/i.test(signature) ||
    encodeFunctionData({ abi: ponsRewardNftABI, functionName: "mint", args: decoded.args }).toLowerCase() !== input.data.toLowerCase()) {
    throw new Error("Social task mint identity or zero fee mismatch");
  }
  return { authorization, signature };
}

/** Task constants and signature are checked on the registered collection, before simulation/send. */
export async function preflightTaskMint(provider: Eip1193Provider, input: PreparedNftTransaction,
  assertCurrent: () => void, options: RpcReadOptions) {
  const { authorization, signature } = assertTaskMintCall(input);
  const read = async (functionName: "TASK_MINT_CAP" | "TASK_TIER_USD" | "TASK_REWARD_MIN_BPS" | "TASK_REWARD_MAX_BPS" | "taskMintCount") => {
    assertCurrent();
    let result: unknown;
    try { result = await readRpc(provider, { method: "eth_call", params: [{ to: input.to,
      data: encodeFunctionData({ abi: ponsRewardNftABI, functionName }) }, "latest"] }, options); }
    catch (error) { assertCurrent(); throw contractReadError(error); }
    assertCurrent();
    if (typeof result !== "string") throw new Error("Invalid task contract response");
    return decodeFunctionResult({ abi: ponsRewardNftABI, functionName, data: result as Hex });
  };
  const [cap, tier, min, max, count] = await Promise.all([
    read("TASK_MINT_CAP"), read("TASK_TIER_USD"), read("TASK_REWARD_MIN_BPS"), read("TASK_REWARD_MAX_BPS"), read("taskMintCount"),
  ]);
  if (cap !== (input.chainId === ELYSIUM.chainId ? BigInt(ELYSIUM.taskMintCap) : 2222n) || tier !== 0n || min !== 5 || max !== 10) throw new Error("Task mint policy differs from the reviewed program");
  if (count >= cap) throw new Error("TaskMintCapReached: All social task NFTs have been claimed.");
  assertCurrent();
  let digest: unknown;
  try { digest = await readRpc(provider, { method: "eth_call", params: [{ to: input.to,
    data: encodeFunctionData({ abi: ponsRewardNftABI, functionName: "mintAuthorizationDigest", args: [authorization] }) }, "latest"] }, options); }
  catch (error) { assertCurrent(); throw contractReadError(error); }
  assertCurrent();
  let recovered: string | null = null;
  if (typeof digest === "string" && /^0x[0-9a-f]{64}$/i.test(digest)) {
    try { recovered = await recoverAddress({ hash: digest as Hex, signature }); }
    catch { /* A malformed signature must never be printed into recovery history. */ }
  }
  if (!recovered || !input.mintSigner || !same(recovered, input.mintSigner)) {
    throw new Error("InvalidAuthorizationSignature: Task authorization does not match the collection signer/domain");
  }
  assertCurrent();
}

function same(a: string, b: string) { return a.toLowerCase() === b.toLowerCase(); }
